import { httpClient, useSession, useAuctionEvent, readMockPlacements } from '@/shell';
import type { BidderItemSummary, MyBidsResponse, Invoice } from '@neon-one/auctions-types';

const MOCK_BID_INCREMENT_CENTS = 2500;

function mockBidderItem(
  itemId: number,
  title: string,
  myBidsStatus: BidderItemSummary['myBidsStatus'],
  amounts: { currentHigh: number; myLast: number; myMax?: number | null },
  pickupInstructions: string | null = null,
): BidderItemSummary {
  return {
    itemId,
    bundleId: null,
    title,
    primaryPhoto: null,
    status: myBidsStatus === 'won' || myBidsStatus === 'lost' ? 'closed' : 'open',
    myMaxAmountCents: amounts.myMax ?? null,
    myLastBidAmountCents: amounts.myLast,
    currentHighBidCents: amounts.currentHigh,
    minimumNextBidCents: amounts.currentHigh + MOCK_BID_INCREMENT_CENTS,
    bidIncrementCents: MOCK_BID_INCREMENT_CENTS,
    closedAt: null,
    myBidsStatus,
    pickupInstructions,
    invoiceUuid: null,
    invoicePaymentStatus: null,
  };
}

// Overlays bids placed during this demo session onto the static fixture,
// so the item-detail bar, catalog pills, and My Bids reflect a placement
// the same way a real /me/bids refetch would. Each recorded entity is
// moved to (or created in) `winning` at the placed amount.
function withSessionPlacements(resp: MyBidsResponse): MyBidsResponse {
  const placements = readMockPlacements();
  if (placements.length === 0) return resp;
  const next: MyBidsResponse = {
    ...resp,
    winning: [...resp.winning],
    losing: [...resp.losing],
    removed: [...(resp.removed ?? [])],
  };
  for (const p of placements) {
    const matches = (row: BidderItemSummary): boolean =>
      p.itemId != null ? row.itemId === p.itemId : p.bundleId != null && row.bundleId === p.bundleId;
    for (const bucket of [next.winning, next.losing, next.removed] as BidderItemSummary[][]) {
      const idx = bucket.findIndex(matches);
      if (idx !== -1) {
        const [row] = bucket.splice(idx, 1);
        if (row && row.myBidsStatus === 'winning') next.spendingCents -= row.currentHighBidCents;
      }
    }
    next.winning.push({
      itemId: p.itemId,
      bundleId: p.bundleId,
      title: p.title,
      primaryPhoto: null,
      status: 'open',
      myMaxAmountCents: p.maxAmountCents,
      myLastBidAmountCents: p.amountCents,
      currentHighBidCents: p.amountCents,
      minimumNextBidCents: p.amountCents + MOCK_BID_INCREMENT_CENTS,
      bidIncrementCents: MOCK_BID_INCREMENT_CENTS,
      closedAt: null,
      myBidsStatus: 'winning',
      ...(p.bundleId != null ? { isBundle: true } : {}),
      invoiceUuid: null,
      invoicePaymentStatus: null,
    });
    next.spendingCents += p.amountCents;
  }
  return next;
}

function mockMyBidsResponsePreClose(): MyBidsResponse {
  return {
    bidderUuid: '00000000-0000-0000-0000-000000000001',
    winning: [
      mockBidderItem(1, 'Napa Valley Weekend for Two', 'winning', {
        currentHigh: 60000, myLast: 60000, myMax: 90000,
      }),
      mockBidderItem(5, 'Private Chef Dinner for Eight', 'winning', {
        currentHigh: 47500, myLast: 47500,
      }),
      mockBidderItem(6, 'Mountain Photography Print, signed', 'winning', {
        currentHigh: 15000, myLast: 15000,
      }),
    ],
    losing: [
      mockBidderItem(3, 'Lakeview Spa Day Package', 'losing', {
        currentHigh: 35000, myLast: 30000,
      }),
    ],
    won: [],
    lost: [],
    removed: [],
    spendingCents: 122500,
    // Offers only exist after the gavel — there is nothing to hold open here.
    outstandingOffers: [],
  };
}

function mockMyBidsResponsePostClose(): MyBidsResponse {
  return {
    bidderUuid: '00000000-0000-0000-0000-000000000001',
    winning: [],
    losing: [],
    won: [
      // Three shapes on purpose: long enough to clamp, short enough not to,
      // and none at all — the Wins card renders a different thing for each.
      mockBidderItem(
        1,
        'Napa Valley Weekend for Two',
        'won',
        { currentHigh: 60000, myLast: 60000 },
        'Your voucher is emailed within 48 hours of payment. Booking is subject to availability. Call the winery directly at least three weeks ahead, and mention the auction reference on your voucher.',
      ),
      mockBidderItem(
        5,
        'Private Chef Dinner for Eight',
        'won',
        { currentHigh: 47500, myLast: 47500 },
        'Contact the chef within 30 days to schedule.',
      ),
      mockBidderItem(6, 'Mountain Photography Print, signed', 'won', {
        currentHigh: 15000, myLast: 15000,
      }),
    ],
    lost: [
      mockBidderItem(3, 'Lakeview Spa Day Package', 'lost', {
        currentHigh: 37500, myLast: 30000,
      }),
    ],
    removed: [],
    spendingCents: 122500,
    // The demo bidder holds no unanswered offer: the offer page reads the real
    // /offers/:uuid, so a seeded callout here would link nowhere.
    outstandingOffers: [],
  };
}

// GT1-442 (bid-void-design.md §6.2) — `?demo=removed` seeds the conditional
// Removed bucket so the section, its copy and the "Removed" pill are
// demoable without an admin actually voiding a bid. Mirrors the server rule:
// the item leaves its active bucket entirely (no surviving active bid) and
// contributes nothing to spending.
function withRemovedDemo(base: MyBidsResponse): MyBidsResponse {
  const removedItem = mockBidderItem(3, 'Lakeview Spa Day Package', 'removed', {
    currentHigh: 32500, myLast: 30000,
  });
  return {
    ...base,
    losing: base.losing.filter(i => i.itemId !== removedItem.itemId),
    lost: base.lost.filter(i => i.itemId !== removedItem.itemId),
    removed: [removedItem],
  };
}

function readDemoFlag(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('demo');
}

type CheckoutDemo = 'card-no-autocharge' | 'no-card' | null;
function readCheckoutDemo(): CheckoutDemo {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('demo');
  if (v === 'card-no-autocharge' || v === 'no-card') return v;
  return null;
}

export const myBidsApi = {
  async fetch(): Promise<MyBidsResponse> {
    const session = useSession();
    const uuid = session.bidder?.uuid;
    if (typeof uuid === 'string' && uuid.startsWith('mock-bidder-')) {
      const isPostClose = useAuctionEvent().event?.status === 'closed';

      if (isPostClose) {
        const { MOCK_INVOICE_UUID } = await import('@/features/checkout/api');
        const { useMyBidsStore } = await import('./store');
        const demo = readCheckoutDemo();
        if (demo && session.bidder) {
          const wantsCard = demo === 'card-no-autocharge';
          if (session.bidder.hasPaymentMethod !== wantsCard) {
            session.setBidder({ ...session.bidder, hasPaymentMethod: wantsCard });
          }
          useMyBidsStore().setMockInvoice({
            uuid: MOCK_INVOICE_UUID,
            totalCents: 122500,
            paymentStatus: 'pending',
            paymentMethod: null,
            paymentReference: null,
          });
        } else {
          if (session.bidder && !session.bidder.hasPaymentMethod) {
            session.setBidder({ ...session.bidder, hasPaymentMethod: true });
          }
          const { useMyBidsStore } = await import('./store');
          useMyBidsStore().setMockInvoice({
            uuid: MOCK_INVOICE_UUID,
            totalCents: 122500,
            paymentStatus: 'paid',
            paymentMethod: null,
            paymentReference: null,
          });
        }
        const postClose = mockMyBidsResponsePostClose();
        return readDemoFlag() === 'removed' ? withRemovedDemo(postClose) : postClose;
      }
      const preClose = mockMyBidsResponsePreClose();
      const withDemoFlag = readDemoFlag() === 'removed' ? withRemovedDemo(preClose) : preClose;
      return withSessionPlacements(withDemoFlag);
    }
    const resp = await httpClient().get<MyBidsResponse>('/me/bids');
    return resp.data;
  },
  async fetchInvoice(invoiceUuid: string): Promise<Invoice> {
    const resp = await httpClient().get<Invoice>(
      `/invoices/${encodeURIComponent(invoiceUuid)}`,
    );
    return resp.data;
  },
};
